import styled from 'styled-components'

import { cardFrameCSS } from '@/@bothrs-ui/web/components/card/Card'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StIconBadge } from '../IconBadge.styled'

import type { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * ContactInfoCard
 * ------------------------------------------------------------------------------------------------- */

type ContactInfoCardRootProps = ComponentPropsWithoutRef<'div'>

const ContactInfoCardRoot = ({ children, ...props }: ContactInfoCardRootProps) => (
  <StCardContainer {...props}>{children}</StCardContainer>
)

/* ------------------------------------------------------------------------------------------------- *
 * ContactInfoCard.Name
 * ------------------------------------------------------------------------------------------------- */

// The text parts take the Span's own props, so a call site can pick a smaller type or another colour
// (the agenda detail screens do) while the defaults stay what the other consumers expect.
type ContactInfoCardTextProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'> & {
  type?: ComponentPropsWithoutRef<typeof Span>['type']
}

type ContactInfoCardNameProps = ContactInfoCardTextProps

const ContactInfoCardName = ({ children, ...props }: ContactInfoCardNameProps) => (
  <Span type='BodyLargeBold' {...props}>
    {children}
  </Span>
)

/* ------------------------------------------------------------------------------------------------- *
 * ContactInfoCard.Phone
 * ------------------------------------------------------------------------------------------------- */

type ContactInfoCardPhoneProps = ComponentPropsWithoutRef<'a'>

const ContactInfoCardPhone = ({ children, ...props }: ContactInfoCardPhoneProps) => (
  <StPhoneLink {...props}>{children}</StPhoneLink>
)

/* ------------------------------------------------------------------------------------------------- *
 * ContactInfoCard.PhoneNumber
 * ------------------------------------------------------------------------------------------------- */

type ContactInfoCardPhoneNumberProps = ContactInfoCardTextProps

const ContactInfoCardPhoneNumber = ({ children, ...props }: ContactInfoCardPhoneNumberProps) => (
  <Span type='BodyLargeRegular' {...props}>
    {children}
  </Span>
)

/* ------------------------------------------------------------------------------------------------- *
 * ContactInfoCard.Icon
 * ------------------------------------------------------------------------------------------------- */

type ContactInfoCardIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const ContactInfoCardIcon = (props: ContactInfoCardIconProps) => <StIconBadge {...props} />

const ContactInfoCard = Object.assign(ContactInfoCardRoot, {
  Name: ContactInfoCardName,
  Phone: ContactInfoCardPhone,
  PhoneNumber: ContactInfoCardPhoneNumber,
  Icon: ContactInfoCardIcon,
})

export { ContactInfoCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StCardContainer = styled.div`
  ${cardFrameCSS()}

  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.UI.SpacingPx.Space3};
  `}
`

const StPhoneLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  color: inherit;
  text-decoration: none;
`
